/**
 * TPG Player — Companion Image + Audio presentation controller (Phase B1).
 *
 * Lab / prototype only until host wiring. Does not touch production HostPlaybackPage.
 * Implements loop modes: none | audio_only | presentation.
 */

import { MediaLoopMode, createCompanionImageAudio } from "./MediaAsset";

/**
 * @param {object} companionConfig
 * @param {object} [hooks]  { onEvent(name, payload), audioElement, imageElement }
 * @returns {object} facade-like controller
 */
export function createCompanionPresentationController(companionConfig, hooks) {
  var config = createCompanionImageAudio(companionConfig || {});
  hooks = hooks || {};
  var audioEl = hooks.audioElement || null;
  var destroyed = false;
  var listeners = [];

  function emit(name, payload) {
    if (hooks.onEvent) hooks.onEvent(name, payload || {});
    listeners.forEach(function (l) {
      if (l.name === name || l.name === "*") l.fn(payload || {});
    });
  }

  function bindAudio(el) {
    audioEl = el;
    if (!audioEl) return;
    audioEl.loop = config.loopMode === MediaLoopMode.AUDIO_ONLY;
    audioEl.addEventListener("play", function () {
      emit("PlaybackStarted", {});
    });
    audioEl.addEventListener("pause", function () {
      emit("PlaybackPaused", {});
    });
    audioEl.addEventListener("ended", function () {
      if (config.loopMode === MediaLoopMode.PRESENTATION) {
        audioEl.currentTime = 0;
        var p = audioEl.play();
        if (p && p.catch) p.catch(function () {});
        emit("PresentationLooped", {});
        return;
      }
      emit("PlaybackEnded", {});
    });
    audioEl.addEventListener("timeupdate", function () {
      emit("TimeUpdate", {
        currentTime: audioEl.currentTime,
        duration: audioEl.duration || 0,
      });
    });
    audioEl.addEventListener("error", function () {
      emit("PlaybackError", { message: "Companion audio failed" });
    });
  }

  return {
    getConfig: function () {
      return config;
    },
    attachAudio: bindAudio,
    play: function () {
      if (destroyed || !audioEl) {
        if (!config.audio && config.image) {
          emit("PresentationReady", { audio: false });
          return Promise.resolve();
        }
        return Promise.reject(new Error("No companion audio element"));
      }
      var p = audioEl.play();
      return p && p.catch ? p : Promise.resolve();
    },
    pause: function () {
      if (audioEl) audioEl.pause();
    },
    seek: function (seconds) {
      if (audioEl && isFinite(seconds)) audioEl.currentTime = seconds;
    },
    setVolume: function (v) {
      if (audioEl) audioEl.volume = Math.max(0, Math.min(1, Number(v)));
    },
    setMuted: function (m) {
      if (audioEl) audioEl.muted = !!m;
    },
    setPlaybackRate: function (r) {
      if (audioEl) audioEl.playbackRate = r;
    },
    getCurrentTime: function () {
      return audioEl ? audioEl.currentTime || 0 : 0;
    },
    getDuration: function () {
      return audioEl ? audioEl.duration || 0 : 0;
    },
    getState: function () {
      return {
        presentation: "companion",
        hasImage: !!config.image,
        hasAudio: !!config.audio,
        loopMode: config.loopMode,
        currentTime: this.getCurrentTime(),
        duration: this.getDuration(),
        paused: audioEl ? audioEl.paused : true,
      };
    },
    on: function (name, fn) {
      listeners.push({ name: name, fn: fn });
    },
    destroy: function () {
      destroyed = true;
      listeners = [];
      audioEl = null;
    },
  };
}

export default createCompanionPresentationController;
